import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import { useUser, useAuth as useClerkAuth } from '@clerk/react';
import { useMutation, useAction } from 'convex/react';
import { useEffect, useState, useRef } from 'react';
import { toast } from '@/template/lib/toast';
import { api } from '../lib/api';
import Layout from '@template/components/layout/Layout';
import OnboardingDialog from '@/components/features/onboarding-dialog';
import { useQuery } from 'convex/react';
// `Link` is unused here
import { useTranslation } from 'react-i18next';
import { getPendingProviderAction, getPendingServiceData, clearPendingProviderAction, clearPendingServiceData } from '@/lib/pending-provider-action';
import { getPendingSubstituteApplication, clearPendingSubstituteApplication } from '@/lib/pending-substitute-application';
import { type OnboardingMe, type OnboardingProfile, type OnboardingServicePreferences } from '@/types/users';
import { storeCurrentAuthRedirect } from '@/lib/auth-redirect';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { trackServiceApplied, trackServiceApplicationFailed } from '@/lib/analytics';
import { type ApplyForJobEventDetail } from '@/types/duty';
import type { Id } from 'convex/_generated/dataModel';

const ProtectedLayout = () => {
  const { isLoaded, isSignedIn, user } = useUser();
  const { isSignedIn: authSignedIn, isLoaded: isAuthLoaded } = useClerkAuth();
  const { t } = useTranslation('common');
  // Ensure Convex user record exists before rendering nested routes
  const updateLastActive = useAction(api.template.users.updateLastActive);
  const applyToDuty = useMutation(api.app.applications.mutations.applyToDuty);
  const [bootstrap, setBootstrap] = useState<{ userId: string; status: 'connecting' | 'error' | 'ready' } | null>(null);
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);
  // placeholder state kept for compatibility
  const [_ensuredOrgId] = useState<string | null>(null);

  // Client-side redirect for legacy internal paths starting with /_protected
  const redirectPath = typeof window !== 'undefined' && window.location.pathname.startsWith('/_protected/') ? window.location.pathname.replace('/_protected', '') : null;

  useEffect(() => {
    if (!authSignedIn || !user?.id) return;
    const userId = user.id;
    let active = true;
    setBootstrap({ userId, status: 'connecting' });

    const fail = () => {
      if (!active) return;
      active = false;
      setBootstrap({ userId, status: 'error' });
    };
    const timeoutId = setTimeout(fail, 10000);

    void updateLastActive({}).then(
      (id) => {
        clearTimeout(timeoutId);
        if (!active) return;
        if (!id) {
          fail();
          return;
        }
        active = false;
        setBootstrap({ userId, status: 'ready' });
      },
      () => {
        clearTimeout(timeoutId);
        fail();
      }
    );

    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, [authSignedIn, user?.id, bootstrapAttempt, updateLastActive]);

  // Global listener for confirmed applications from ApplyModal
  useEffect(() => {
    const handler = async (e: Event) => {
      const detail = ((e as CustomEvent<ApplyForJobEventDetail>).detail || {}) as Partial<ApplyForJobEventDetail>;
      const { jobId, proposedCompensation, validityDays, isVerified } = detail;
      if (!jobId) return;
      try {
        // DA-434: Pass validityDays to the mutation
        const applicationId = await applyToDuty({
          dutyId: jobId as Id<'duties'>,
          ...(typeof proposedCompensation === 'number' ? { proposedCompensation } : {}),
          ...(validityDays ? { validityDays } : {}),
        });
        trackServiceApplied({
          service_id: String(jobId),
          application_id: applicationId ? String(applicationId) : undefined,
          service_type: detail.serviceType,
          compensation_type: detail.compensationType,
          source: detail.source ?? 'unknown',
        });
        // DA-463: Show different toast message based on verification status
        if (isVerified === false) {
          toast.success(t('toasts.protected.applicationSavedPendingVerification'));
        } else {
          toast.success(t('toasts.protected.applicationSubmitted'));
        }
      } catch (err: unknown) {
        trackServiceApplicationFailed(err, {
          service_id: String(jobId),
          service_type: detail.serviceType,
          compensation_type: detail.compensationType,
          source: detail.source ?? 'unknown',
        });
        const errorMsg = (err as { message?: string })?.message || t('toasts.protected.applicationFailed');
        toast.error(errorMsg);
      }
    };

    window.addEventListener('applyForJob', handler as EventListener);
    return () => window.removeEventListener('applyForJob', handler as EventListener);
  }, [applyToDuty, t]);

  // No organization mapping in single-tenant mode

  // Show loading while checking authentication
  if (!isLoaded || !isAuthLoaded) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center'>
          <LoadingSpinner size='lg' className='mb-4 mx-auto' />
          <p className='text-muted-foreground'>{t('loading.auth')}</p>
        </div>
      </div>
    );
  }

  // If not signed in to Clerk, redirect to auth
  if (!isSignedIn) {
    storeCurrentAuthRedirect();
    return <Navigate to='/auth' />;
  }

  // Apply legacy URL cleanup after auth check
  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  // If signed in but still bootstrapping backend setup, show connecting state
  if (bootstrap?.userId !== user?.id || bootstrap?.status !== 'ready') {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='w-full max-w-md px-4 text-center'>
          {bootstrap?.userId === user?.id && bootstrap?.status === 'error' ? (
            <>
              <Alert variant='destructive' className='mb-4 text-left'>
                <AlertCircle className='h-4 w-4' aria-hidden='true' />
                <AlertDescription className='text-base'>{t('loading.connectingError')}</AlertDescription>
              </Alert>
              <div className='flex flex-wrap justify-center gap-3'>
                <Button variant='secondary' className='min-h-11' onClick={() => setBootstrapAttempt((attempt) => attempt + 1)}>{t('loading.retryConnection')}</Button>
                <Button variant='outline' className='min-h-11' nativeButton={false} role='link' render={<a href='mailto:support@meindienstarzt.de' />}>{t('loading.contactSupport')}</Button>
              </div>
            </>
          ) : (
            <div role='status'>
              <Loader2 className='animate-spin text-primary size-8 mb-4 mx-auto' aria-hidden='true' />
              <p className='text-muted-foreground'>{t('loading.connecting')}</p>
              <p className='text-sm text-muted-foreground/70 mt-2'>{t('loading.connectingHint')}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Layout renders children; we expose entry links based on role
  return (
    <>
      <Layout />
      {/* Onboarding gate: open modal if required fields are missing */}
      <OnboardingGate open={showOnboarding} setOpen={setShowOnboarding} />
      {/* ApplyModal removed from global mount; it will be added to customer applications page */}
    </>
  );
};

export const Route = createFileRoute('/_protected')({
  loader: async () => {
    // const { default: i18n } = await import('../i18n');
    // await i18n.loadNamespaces?.(['common', 'nav']);
    return null;
  },
  component: ProtectedLayout,
});

export const OnboardingGate: React.FC<{
  open: boolean;
  setOpen: (v: boolean) => void;
}> = ({ open, setOpen }) => {
  const navigate = useNavigate();
  const meRaw = useQuery(api.app.users.queries.getMe, {});
  const meResult = meRaw as unknown as OnboardingMe | undefined;
  const profileRaw = useQuery(api.app.users.queries.getMyUserProfile, {});
  const profileResult = profileRaw as unknown as OnboardingProfile | undefined;
  const servicePrefsRaw = useQuery(api.app.profileMutations.getServiceAreaPreferences, {});
  const servicePrefsResult = servicePrefsRaw as unknown as OnboardingServicePreferences | undefined;
  // DA-387: Query business locations for providers (their address is stored there, not in me.address)
  const businessLocationsRaw = useQuery(api.app.businessLocations.queries.listMyBusinessLocations, meResult?.role === 'provider' ? {} : ('skip' as any));
  const businessLocationsResult = businessLocationsRaw as unknown as Array<{ practiceAddress?: { street?: string; postalCode?: string; city?: string } }> | undefined;

  // DA-438: Mutation to mark when modal was first shown
  const markModalShown = useMutation(api.app.users.mutations.markOnboardingModalShown);
  const createOrResumeProviderServiceDraft = useMutation(api.app.providerServiceDrafts.createOrResumeProviderServiceDraft);

  // Track if user manually dismissed the modal
  const [userDismissed, setUserDismissed] = useState(false);
  // DA-438: Track if we've already called markModalShown to avoid duplicate calls
  const [hasMarkedShown, setHasMarkedShown] = useState(false);
  // DA-436: Track if we've already navigated to avoid duplicate navigations
  const hasNavigatedRef = useRef(false);
  // DA-438 fix: Latch the first-login decision for the lifetime of this mount.
  // markModalShown() below sets onboardingModalShownAt, and getMe is a reactive
  // query, so recomputing isFirstLogin from live data flips it to false the
  // instant we open the modal and immediately re-closes it. Capture it once.
  const isFirstLoginRef = useRef<boolean | null>(null);

  useEffect(() => {
    const me = meResult;
    if (!me) return;

    // DA-343: Support providers in onboarding
    if (me.role !== 'substitute' && me.role !== 'provider') {
      if (open) setOpen(false);
      return;
    }

    // DA-438: Only show modal on FIRST LOGIN
    // First login = neither onboardingModalShownAt nor onboardingCompletedAt is set
    // After first login, profile completion banner handles incomplete profiles
    // DA-438 fix: latch on first evaluation so our own markModalShown() write does
    // not reactively flip this to false and immediately re-close the modal.
    if (isFirstLoginRef.current === null) {
      isFirstLoginRef.current = !me.onboardingModalShownAt && !me.onboardingCompletedAt;
    }
    const isFirstLogin = isFirstLoginRef.current;

    if (!isFirstLogin) {
      // Not first login - NEVER show modal (let ProfileCompletionBanner handle it)
      if (open) setOpen(false);
      return;
    }

    // Wait for data to load
    if (profileResult === undefined || servicePrefsResult === undefined) return;
    // DA-387: Wait for business locations to load for providers
    if (me.role === 'provider' && businessLocationsResult === undefined) return;

    const profile = profileResult ?? null;
    const prefs = servicePrefsResult ?? null;

    // DA-343: Different requirements for providers vs substitutes
    let needs = false;

    if (me.role === 'provider') {
      // DA-387: Provider needs basic personal info and a business location with address
      const missingPersonal = !me.firstName || !me.lastName;
      // Check if provider has a business location with a valid address
      const hasBusinessLocation = businessLocationsResult && businessLocationsResult.length > 0;
      const firstLocation = hasBusinessLocation ? businessLocationsResult[0] : null;
      const missingAddress = !firstLocation?.practiceAddress?.street || !firstLocation?.practiceAddress?.postalCode || !firstLocation?.practiceAddress?.city;
      needs = !me.onboardingCompletedAt || missingPersonal || missingAddress;
    } else {
      // Substitute needs full profile
      const missingProfile = profile === null;
      const missingPersonal = !me.firstName || !me.lastName || !profile?.birthday;
      const missingAddress = !me.address?.street || !me.address?.postalCode || !me.address?.city;
      const missingPreferences = !prefs || (prefs.preferredServiceTypes?.length ?? 0) === 0;
      needs = missingProfile || !me.onboardingCompletedAt || missingPersonal || missingAddress || missingPreferences;
    }

    // Only auto-open if needed AND user hasn't explicitly dismissed it
    if (needs && !userDismissed) {
      setOpen(true);
      // DA-438: Mark modal as shown on first login (idempotent)
      if (!hasMarkedShown) {
        setHasMarkedShown(true);
        void markModalShown({});
      }
    } else if (open && !needs) {
      setOpen(false);
      setUserDismissed(false); // Reset dismissed flag when onboarding is complete
    }
  }, [meResult, profileResult, servicePrefsResult, businessLocationsResult, open, setOpen, userDismissed, markModalShown, hasMarkedShown]);

  // DA-436: Convert marketing-origin provider intent into a durable server draft after onboarding
  useEffect(() => {
    const me = meResult;
    if (!me) return;

    // Only trigger for providers who have completed onboarding
    if (me.role !== 'provider') return;
    if (!me.onboardingCompletedAt) return;

    // When onboarding completes and modal closes, create or resume the provider draft.
    if (!open && !hasNavigatedRef.current) {
      const pendingAction = getPendingProviderAction();
      const pendingServiceData = getPendingServiceData();

      if (pendingAction?.action !== 'createService' || !pendingServiceData?.serviceType) {
        return;
      }

      hasNavigatedRef.current = true;

      void createOrResumeProviderServiceDraft({
        payload: {
          sourceKey: pendingServiceData.sourceKey?.trim() || `legacy_${pendingServiceData.timestamp}`,
          serviceType: pendingServiceData.serviceType,
          startDate: pendingServiceData.startDate,
          endDate: pendingServiceData.endDate,
          compensationType: pendingServiceData.compensationType,
          hasNotfallscheinbeteiligung: pendingServiceData.hasNotfallscheinbeteiligung,
          ...(pendingServiceData.serviceAreaId ? { serviceAreaId: pendingServiceData.serviceAreaId } : {}),
          ...(pendingServiceData.serviceAreaName ? { serviceAreaName: pendingServiceData.serviceAreaName } : {}),
          ...(pendingServiceData.kvRegion ? { kvRegion: pendingServiceData.kvRegion } : {}),
          ...(pendingServiceData.manualServiceArea ? { manualServiceArea: pendingServiceData.manualServiceArea } : {}),
          ...(pendingServiceData.manualBereitschaftspraxis
            ? {
                manualBereitschaftspraxis: pendingServiceData.manualBereitschaftspraxis,
              }
            : {}),
          ...(pendingServiceData.manualFahrdienstgebiet
            ? {
                manualFahrdienstgebiet: pendingServiceData.manualFahrdienstgebiet,
              }
            : {}),
          ...(pendingServiceData.fixedPrice !== undefined ? { fixedPrice: pendingServiceData.fixedPrice } : {}),
          ...(pendingServiceData.minPrice !== undefined ? { minPrice: pendingServiceData.minPrice } : {}),
          ...(pendingServiceData.maxPrice !== undefined ? { maxPrice: pendingServiceData.maxPrice } : {}),
          ...(pendingServiceData.notfallscheinbeteiligungRate !== undefined
            ? {
                notfallscheinbeteiligungRate: pendingServiceData.notfallscheinbeteiligungRate,
              }
            : {}),
          ...(pendingServiceData.notes ? { notes: pendingServiceData.notes } : {}),
        },
      })
        .then((result) => {
          clearPendingServiceData();
          clearPendingProviderAction();

          setTimeout(() => {
            void navigate({
              to: '/provider/dashboard',
              search: { draftId: String(result.draftId) },
              replace: true,
            });
          }, 300);
        })
        .catch((error: unknown) => {
          hasNavigatedRef.current = false;
          const message = error instanceof Error ? error.message : 'Der Entwurf konnte nicht gespeichert werden.';
          toast.error(message);
        });
    }
  }, [createOrResumeProviderServiceDraft, open, meResult?.onboardingCompletedAt, meResult?.role, navigate]);

  // DA-431: Handle post-onboarding navigation for substitute homepage checkout flow
  useEffect(() => {
    const me = meResult;
    if (!me) return;

    // Only trigger for substitutes who have completed onboarding
    if (me.role !== 'substitute') return;
    if (!me.onboardingCompletedAt) return;

    // When onboarding completes and modal closes, check for pending application
    if (!open && !hasNavigatedRef.current) {
      const pendingApplication = getPendingSubstituteApplication();
      if (pendingApplication?.serviceId) {
        hasNavigatedRef.current = true;
        clearPendingSubstituteApplication();
        // Small delay to let onboarding modal close animation complete
        setTimeout(() => {
          void navigate({
            to: '/substitute/$id',
            params: { id: pendingApplication.serviceId },
            search: { openApply: true },
            replace: true,
          });
        }, 300);
      }
    }
  }, [open, meResult?.onboardingCompletedAt, meResult?.role, navigate]);

  // Wrap setOpen to track manual dismissal
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // User explicitly closed the modal
      setUserDismissed(true);
    }
  };

  return <OnboardingDialog open={open} onOpenChange={handleOpenChange} me={meResult ?? null} profile={profileResult ?? null} servicePreferences={servicePrefsResult ?? null} />;
};
